import { LIGHT_REACH_APPLICANT_TYPE } from "~/api/dto/light-reach.dto"
import type { LightReachCreditApplicationRequestDto } from "~/api/dto/light-reach.dto"

/**
 * The LightReach application form's rules, with no React and no lender call in
 * them.
 *
 * Two surfaces post this application: the seller's authenticated brochure and a
 * public presentation link. They differ in who they trust — the public path
 * lets the applicant supply only their SSN, their co-applicant and which
 * disclosures they ticked, because everything else is a fact of the Proposal —
 * but they must not differ in what "this form is complete" means, or in which
 * disclosures count as required. So both read those answers from here.
 */

/** The one disclosure shape both surfaces render, and the only fields they use. */
export type LightReachFormDisclosure = {
  id: string
  name: string
  type: string
  version: string
  text: string
  requireConsent: boolean
}

/**
 * Consent is held as the set of ids the person ticked, not as a copy of the
 * disclosure list with a flag on each entry.
 *
 * The form used to seed one acceptance record per disclosure in an Effect once
 * the fetch landed, which meant the type and version it posted were whatever
 * they had been at seed time. Ids are the only part the person actually
 * chose; everything else is rebuilt from the disclosures on screen at submit,
 * so the record always attests the document they were shown.
 */
export type LightReachFormData = {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  address1: string
  city: string
  state: string
  zip: string
  ssn: string
  acceptedDisclosureIds: readonly string[]
  showCoApplicant: boolean
  coFirstName: string
  coLastName: string
  coPhoneNumber: string
  coEmail: string
  coSsn: string
  coAcceptedDisclosureIds: readonly string[]
}

export type LightReachPrefill = {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  address1: string
  city: string
  state: string
  zip: string
}

export const initialLightReachForm = (
  prefill: LightReachPrefill,
): LightReachFormData => ({
  ...prefill,
  ssn: ``,
  acceptedDisclosureIds: [],
  showCoApplicant: false,
  coFirstName: ``,
  coLastName: ``,
  coPhoneNumber: ``,
  coEmail: ``,
  coSsn: ``,
  coAcceptedDisclosureIds: [],
})

/** Which of the two consent sets a checkbox belongs to. */
export type LightReachApplicantRole = `applicant` | `coApplicant`

const ACCEPTED_IDS_FIELD: Record<
  LightReachApplicantRole,
  `acceptedDisclosureIds` | `coAcceptedDisclosureIds`
> = {
  applicant: `acceptedDisclosureIds`,
  coApplicant: `coAcceptedDisclosureIds`,
}

export const setDisclosureAcceptance = (
  formData: LightReachFormData,
  role: LightReachApplicantRole,
  disclosureId: string,
  accepted: boolean,
): LightReachFormData => {
  const field = ACCEPTED_IDS_FIELD[role]
  const current = formData[field]
  const next = accepted
    ? current.includes(disclosureId)
      ? current
      : [...current, disclosureId]
    : current.filter((id) => id !== disclosureId)

  return next === current ? formData : { ...formData, [field]: next }
}

/** Clearing the co-applicant clears their consent too; it was given as them. */
export const clearCoApplicant = (
  formData: LightReachFormData,
): LightReachFormData => ({
  ...formData,
  showCoApplicant: false,
  coFirstName: ``,
  coLastName: ``,
  coPhoneNumber: ``,
  coEmail: ``,
  coSsn: ``,
  coAcceptedDisclosureIds: [],
})

export const SSN_ERROR = `SSN must be either 4 or 9 digits`

/** LightReach takes the last four or the full nine. This is not Concert's rule. */
export const validateLightReachSsn = (ssn: string): string | undefined => {
  const digitsOnly = ssn.replace(/\D/g, ``)
  return digitsOnly.length === 4 || digitsOnly.length === 9
    ? undefined
    : SSN_ERROR
}

const isFilled = (value: string): boolean => value.trim() !== ``

/** Every disclosure LightReach requires consent for, ticked by this person. */
export const lightreachConsentGiven = (
  acceptedIds: readonly string[],
  disclosures: readonly LightReachFormDisclosure[],
): boolean => {
  const accepted = new Set(acceptedIds)
  return disclosures
    .filter((disclosure) => disclosure.requireConsent)
    .every((required) => accepted.has(required.id))
}

/**
 * Whether the form is complete enough to submit.
 *
 * Deliberately not "is it valid" — the gateway decides that, and on the public
 * surface it is the only thing that can. This is the button's rule: it stops
 * someone spending a submission on a form they can see is half-filled.
 */
export const lightreachFormComplete = (
  formData: LightReachFormData,
  disclosures: readonly LightReachFormDisclosure[],
): boolean => {
  const hasApplicant =
    [
      formData.firstName,
      formData.lastName,
      formData.email,
      formData.phoneNumber,
      formData.address1,
      formData.city,
      formData.state,
      formData.zip,
      formData.ssn,
    ].every(isFilled) &&
    lightreachConsentGiven(formData.acceptedDisclosureIds, disclosures)

  if (!formData.showCoApplicant) return hasApplicant

  return (
    hasApplicant &&
    [
      formData.coFirstName,
      formData.coLastName,
      formData.coEmail,
      formData.coPhoneNumber,
      formData.coSsn,
    ].every(isFilled) &&
    lightreachConsentGiven(formData.coAcceptedDisclosureIds, disclosures)
  )
}

/**
 * The acceptance records for one person, rebuilt from the disclosures on
 * screen. Only what they ticked is sent, in document order, which is what this
 * form has always posted.
 */
const acceptedRecords = (
  disclosures: readonly LightReachFormDisclosure[],
  acceptedIds: readonly string[],
): Array<{
  accepted: boolean
  id: string
  type: string
  version: string
}> => {
  const accepted = new Set(acceptedIds)
  return disclosures
    .filter((disclosure) => accepted.has(disclosure.id))
    .map((disclosure) => ({
      id: disclosure.id,
      type: disclosure.type,
      version: disclosure.version,
      accepted: true,
    }))
}

const digitsOnly = (value: string): string => value.replace(/\D/g, ``)

/**
 * The body the seller route posts.
 *
 * LightReach files the co-applicant against the primary's address, which is the
 * subject property. That has always been what this form sends.
 */
export const buildLightReachApplicationBody = (
  formData: LightReachFormData,
  disclosures: readonly LightReachFormDisclosure[],
): LightReachCreditApplicationRequestDto => {
  const address = {
    address1: formData.address1,
    city: formData.city,
    state: formData.state,
    zip: formData.zip,
  }

  const applicants = [
    {
      type: LIGHT_REACH_APPLICANT_TYPE.PRIMARY,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phoneNumber: digitsOnly(formData.phoneNumber),
      email: formData.email,
      address,
      ssn: digitsOnly(formData.ssn),
      disclosures: acceptedRecords(disclosures, formData.acceptedDisclosureIds),
    },
  ]

  if (formData.showCoApplicant) {
    applicants.push({
      type: LIGHT_REACH_APPLICANT_TYPE.COAPPLICANT,
      firstName: formData.coFirstName,
      lastName: formData.coLastName,
      phoneNumber: digitsOnly(formData.coPhoneNumber),
      email: formData.coEmail,
      address,
      ssn: digitsOnly(formData.coSsn),
      disclosures: acceptedRecords(
        disclosures,
        formData.coAcceptedDisclosureIds,
      ),
    })
  }

  return { applicants }
}

/** One disclosure the applicant ticked, with the version they were shown. */
export type AcceptedDisclosure = { id: string; version: string }

export type PublicLightReachApplicationBody = {
  applicant: { acceptedDisclosures: AcceptedDisclosure[]; ssn: string }
  coApplicant?: {
    acceptedDisclosures: AcceptedDisclosure[]
    email: string
    firstName: string
    lastName: string
    phoneNumber: string
    ssn: string
  }
}

/**
 * What a public presentation link posts instead.
 *
 * Far narrower than the seller body, and narrower than it looks: no name, no
 * email, no phone, no address for the primary applicant, because those are the
 * Proposal's and the gateway reads them there. And no disclosure `type` for
 * anyone — only the id that was ticked and the version that was on screen when
 * it was. The gateway rebuilds every acceptance record from the document set it
 * fetched itself and refuses a version that is no longer current, so a consent
 * record can neither be invented nor quietly attest wording nobody read.
 */
export const buildPublicLightReachApplicationBody = (
  formData: LightReachFormData,
  disclosures: readonly LightReachFormDisclosure[],
): PublicLightReachApplicationBody => {
  // Built from what is on screen, so the version sent is the version rendered
  // and an id left over from a disclosure set that has since rotated is not
  // sent at all.
  const accepted = (ids: readonly string[]): AcceptedDisclosure[] => {
    const ticked = new Set(ids)
    return disclosures
      .filter((disclosure) => ticked.has(disclosure.id))
      .map((disclosure) => ({ id: disclosure.id, version: disclosure.version }))
  }

  return {
    applicant: {
      ssn: digitsOnly(formData.ssn),
      acceptedDisclosures: accepted(formData.acceptedDisclosureIds),
    },
    ...(formData.showCoApplicant
      ? {
          coApplicant: {
            firstName: formData.coFirstName.trim(),
            lastName: formData.coLastName.trim(),
            email: formData.coEmail.trim(),
            phoneNumber: digitsOnly(formData.coPhoneNumber),
            ssn: digitsOnly(formData.coSsn),
            acceptedDisclosures: accepted(formData.coAcceptedDisclosureIds),
          },
        }
      : {}),
  }
}
